
/**
 * Utility functions for timezone handling and Stockholm daytime detection
 */

/**
 * Check if the current time is during daytime hours in Stockholm (8 AM - 8 PM)
 */
export function isStockholmDaytime(): boolean {
  const now = new Date();
  
  // Convert current time to Stockholm timezone
  const stockholmTime = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Stockholm',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false
  }).format(now);
  
  const [hourStr, minuteStr] = stockholmTime.split(':');
  const stockholmHour = parseInt(hourStr);
  const stockholmMinute = parseInt(minuteStr);
  
  // Consider daytime between 8 AM and 8 PM (20:00)
  const totalMinutes = stockholmHour * 60 + stockholmMinute;
  const startMinutes = 8 * 60; // 8:00 AM
  const endMinutes = 20 * 60;  // 8:00 PM
  
  return totalMinutes >= startMinutes && totalMinutes < endMinutes;
}

/**
 * Check if a timestamp is older than the specified minutes
 */
export function isOlderThan(timestamp: string | undefined, minutes: number): boolean {
  if (!timestamp) return true;
  
  const lastUpdate = new Date(timestamp);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60));
  
  return diffInMinutes > minutes;
}

/**
 * Get Stockholm time as ISO string for display purposes
 */
export function getStockholmTimeString(): string {
  const now = new Date();
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(now).replace(' ', 'T') + ':00.000Z';
}

/**
 * Check if it's currently sunny hours (between 6 AM and 10 PM Stockholm time)
 * Expanded window to allow updates during more hours
 */
export function isStockholmSunnyHours(): boolean {
  const now = new Date();
  
  const stockholmTime = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Stockholm',
    hour: 'numeric',
    hour12: false
  }).format(now);
  
  const stockholmHour = parseInt(stockholmTime);
  
  // Expanded hours for sun status updates (6 AM to 10 PM)
  return stockholmHour >= 6 && stockholmHour < 22;
}

/**
 * Get current Stockholm hour for debugging
 */
export function getStockholmHour(): number {
  const now = new Date();
  const stockholmTime = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Stockholm',
    hour: 'numeric',
    hour12: false
  }).format(now);
  
  return parseInt(stockholmTime);
}

/**
 * Check if we're in a valid update window with detailed logging
 */
export function canUpdateSunStatus(reason = 'check'): { canUpdate: boolean; reason: string; stockholmHour: number } {
  const stockholmHour = getStockholmHour();
  const canUpdate = stockholmHour >= 6 && stockholmHour < 22;
  
  const result = {
    canUpdate,
    stockholmHour,
    reason: canUpdate 
      ? `Update allowed at Stockholm hour ${stockholmHour}` 
      : `Update blocked: Stockholm hour ${stockholmHour} outside 6-22 window`
  };
  
  return result;
}
